import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, Euro, Briefcase, Dumbbell, Layers, User, Shield, LogOut, BarChart3, Users, ClipboardCheck, FlaskConical, Compass, MessageSquareText, Info, ScrollText, FileLock2, KeyRound, BookOpen, CircleHelp } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useUser } from "@/context/UserContext";
import { WachtwoordWijzigenDialog } from "@/components/WachtwoordWijzigenDialog";

const ADMIN_ROLLEN = ["beheerder", "superbeheerder"];

const Sectie = ({ titel, children }) => (
  <div className="mb-5">
    <p className="text-[11px] font-bold uppercase tracking-wider text-[#9C8467] mb-1.5 px-3">{titel}</p>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const MenuLink = ({ to, icon: Icon, label, testid, onNavigate }) => (
  <NavLink to={to} data-testid={testid} onClick={onNavigate}
    className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-[#FBF3E3] text-[#8a5a20]" : "text-slate-700 hover:bg-slate-100"}`}>
    <Icon className="w-4 h-4 text-[#B9772B]" /> {label}
  </NavLink>
);

export const HoofdMenu = () => {
  const [open, setOpen] = useState(false);
  const [wwDialoog, setWwDialoog] = useState(false);
  const { user, authEnabled, logout } = useUser();
  const sluit = () => setOpen(false);
  const isAdmin = ADMIN_ROLLEN.includes(user?.rol);
  const isSuper = user?.rol === "superbeheerder";
  return (
    <>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="hoofdmenu-btn" aria-label="Hoofdmenu"
          className="p-2 -ml-1 rounded-md text-slate-600 hover:bg-slate-100 transition-colors">
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0 overflow-y-auto" aria-describedby={undefined} data-testid="hoofdmenu-panel">
        <SheetHeader className="px-4 py-4 border-b border-[#EBE1D2] text-left">
          <SheetTitle className="flex items-center gap-2 font-heading text-[#3A281B]">
            <span className="w-8 h-8 rounded-lg zk-brand-gradient flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></span>
            Zoetwaren Kompas
          </SheetTitle>
        </SheetHeader>
        <nav className="px-2 py-4">
          <div className="mb-5 space-y-0.5">
            <NavLink to="/" data-testid="menu-stel-vraag" onClick={sluit}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-[#FBF3E3] text-[#8a5a20]" : "text-[#4A3122] hover:bg-slate-100"}`}>
              <MessageSquareText className="w-4 h-4 text-[#B9772B]" /> Stel een vraag
            </NavLink>
          </div>
          <Sectie titel="Hulpmiddelen">
            <MenuLink to="/salaris" icon={Euro} label="Salarischeck" testid="menu-salaris" onNavigate={sluit} />
            <MenuLink to="/functie" icon={Briefcase} label="Functiecheck" testid="menu-functie" onNavigate={sluit} />
            <MenuLink to="/tilcheck" icon={Dumbbell} label="Tilcheck" testid="menu-tilcheck" onNavigate={sluit} />
            <MenuLink to="/functiehandboek" icon={BookOpen} label="Functiehandboek" testid="menu-functiehandboek" onNavigate={sluit} />
            <MenuLink to="/cao" icon={ScrollText} label="CAO-wijzer" testid="menu-cao-wijzer" onNavigate={sluit} />
            {/* PILOTMAATREGEL v2: OR-analyse volledig uit de pilot (menu-item tijdelijk verwijderd) */}
          </Sectie>
          {isAdmin && (
            <Sectie titel="Informatie">
              <MenuLink to="/bronnen" icon={Layers} label="Bronnen" testid="menu-bronnen" onNavigate={sluit} />
            </Sectie>
          )}
          <Sectie titel="Mijn account">
            <MenuLink to="/mijn" icon={User} label="Mijn omgeving (profiel)" testid="menu-profiel" onNavigate={sluit} />
            <MenuLink to="/mijn?tab=privacy" icon={Shield} label="Privacy & gegevens" testid="menu-privacy" onNavigate={sluit} />
            {authEnabled && user?.auth_provider !== "google" && (
              <button data-testid="menu-wachtwoord-wijzigen" onClick={() => { sluit(); setWwDialoog(true); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors">
                <KeyRound className="w-4 h-4 text-[#B9772B]" /> Wachtwoord wijzigen
              </button>
            )}
          </Sectie>
          {isSuper && (
            <Sectie titel="Beheer">
              <MenuLink to="/dashboard" icon={BarChart3} label="Dashboard" testid="menu-dashboard" onNavigate={sluit} />
              <MenuLink to="/gebruikers" icon={Users} label="Gebruikersbeheer" testid="menu-gebruikers" onNavigate={sluit} />
              {isAdmin && <MenuLink to="/functie-experiment" icon={FlaskConical} label="Functiecheck-experiment" testid="menu-experiment" onNavigate={sluit} />}
            </Sectie>
          )}
          <Sectie titel="Over & juridisch">
            <MenuLink to="/faq" icon={CircleHelp} label="FAQ & Contact" testid="menu-faq-contact" onNavigate={sluit} />
            <MenuLink to="/over" icon={Info} label="Over Zoetwaren Kompas" testid="menu-over" onNavigate={sluit} />
            <MenuLink to="/voorwaarden" icon={ScrollText} label="Algemene Voorwaarden" testid="menu-voorwaarden" onNavigate={sluit} />
            <MenuLink to="/privacy" icon={FileLock2} label="Privacyverklaring" testid="menu-privacyverklaring" onNavigate={sluit} />
          </Sectie>
          {authEnabled && (
            <div className="border-t border-[#EBE1D2] pt-3 mt-2">
              <button data-testid="menu-uitloggen" onClick={() => { sluit(); logout(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors">
                <LogOut className="w-4 h-4 text-slate-500" /> Uitloggen
              </button>
            </div>
          )}
        </nav>
      </SheetContent>
    </Sheet>
    <WachtwoordWijzigenDialog open={wwDialoog} onOpenChange={setWwDialoog} />
    </>
  );
};
